/** IXD 편집기 - 다운로드 완료 사이드 패널 (Figma 7206:570419) */

import { useEffect, useRef } from "react";
import { Download, Loader2, MoveHorizontal, Sparkle, X } from "lucide-react";
import type { IxdDownloadFile } from "@/apis/ixd-editor/types";
import { existingReportEntryUrl } from "@/utils/ixd-dsd-compare/comparison-url";
import { FileFormatIcon, formatSize } from "@/components/file-drop-zone";
import { Sheet, SheetContent, SheetTitle } from "@/styles/ui/sheet";
import { Button } from "@/toggle-ui-kit/button";
import { DIMMER_STYLE } from "@/toggle-ui-kit/decorate/dimmer";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { logIxdEditorAction } from "@/utils/usage-log";

export const DownloadCompletePanel = ({
  file,
  reportId,
  isLocked = false,
  isRedownloading = false,
  onRedownload,
  onClose,
}: {
  /** 방금 내려받은 파일. 없으면 패널을 띄우지 않는다 */
  file: Pick<IxdDownloadFile, "fileName" | "fileSize"> | null;
  /** 지금 편집 중인 IXD. DSD↔IXD 대사 진입 시 이 IXD 가 고른 상태로 열린다 */
  reportId: string | null;
  /**
   * 재요청·재조회 중. 패널이 요청 중에도 떠 있으므로(mutation.data 대신 downloadedFile 사용)
   * 이 가드가 없으면 재패킹·중복 저장을 또 돌릴 수 있다 — 헤더 다운로드 버튼과 같은 조건.
   */
  isLocked?: boolean;
  /** 재요청이 실제로 도는 중 — 스피너 전용 */
  isRedownloading?: boolean;
  onRedownload: () => void;
  onClose: () => void;
}) => {
  // radix 는 slide-out(300ms) 동안 Content 를 마운트해 두므로, file 이 null 이 된 뒤에도
  // 마지막으로 보여준 파일을 그려야 카드가 빈 채로 닫히지 않는다.
  const shownRef = useRef(file);
  if (file) shownRef.current = file;
  const shown = shownRef.current;

  // 열린 순간만 센다 — 열린 채 다시 다운로드하면 file 객체만 바뀌므로 열림 여부에 건다
  const isOpen = !!file;
  useEffect(() => {
    if (isOpen) logIxdEditorAction("다운로드 완료 패널 열림");
  }, [isOpen]);

  return (
    <Sheet open={!!file} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        hideCloseButton
        aria-describedby={undefined}
        overlayClassName={DIMMER_STYLE}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex w-[400px] max-w-none flex-col items-end gap-16 border-l border-line-normal bg-white px-6 pb-3 pt-6 shadow-large sm:max-w-none"
      >
        <IconButton
          variant="transparent"
          size={36}
          aria-label="닫기"
          icon={<X className="size-6" />}
          onClick={onClose}
        />
        <div className="flex w-full flex-col gap-4">
          <SheetTitle asChild>
            <h2 className="heading-2-semibold text-label-normal">
              파일 다운로드가 완료되었습니다.
            </h2>
          </SheetTitle>

          <div className="flex w-full flex-col items-end rounded-[8px] bg-background-gray-alternative p-4">
            <div className="flex w-full min-w-0 flex-col gap-1">
              <p className="label-1-normal-regular truncate text-label-normal">
                {shown?.fileName}
              </p>
              {shown?.fileSize != null && (
                <p className="caption-1-regular truncate text-label-alternative">
                  {formatSize(shown.fileSize)}
                </p>
              )}
            </div>
            <Button
              variant="outlined"
              color="secondary"
              size="base"
              disabled={isLocked}
              leftIcon={
                isRedownloading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Download className="size-4" />
                )
              }
              onClick={() => {
                logIxdEditorAction("다시 다운로드");
                onRedownload();
              }}
            >
              다시 다운로드
            </Button>
          </div>

          <div className="flex w-full flex-col gap-4 rounded-[8px] border-2 border-amber-100 bg-amber-100 p-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Sparkle className="size-[18px] text-orange-500" />
                <span className="label-1-normal-semibold text-orange-500">
                  추천 기능
                </span>
              </div>
              <p className="label-1-normal-regular text-label-neutral">
                지금 작업한 파일 그대로{" "}
                <span className="label-1-normal-semibold text-label-normal">
                  최종 검토
                </span>
                까지 이어서 확인할 수 있습니다. 제출 전에 한 번 더 확인해보세요.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 rounded-[12px] bg-white p-2">
              <div className="flex w-full items-center justify-center gap-6 rounded-[8px] bg-background-gray-alternative p-3">
                <FileFormatIcon format="dsd" />
                <MoveHorizontal className="size-[18px] text-label-normal" />
                <FileFormatIcon format="xbrl" />
              </div>
              <div className="flex flex-col gap-0.5 px-2">
                <p className="label-1-normal-semibold text-label-normal">
                  보고서 최종 검토
                </p>
                <p className="label-2-regular text-label-alternative">
                  단위 차이까지 포함해 일치 · 불일치 항목을 한눈에 확인하실 수
                  있습니다.
                </p>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="outlined"
              color="secondary"
              size="base"
              onClick={onClose}
            >
              나중에 하기
            </Button>
            <Button
              variant="solid"
              color="blue"
              size="base"
              // 편집기를 떠나지 않고 최종 검토를 본다 — 미저장 변경 가드와도 부딪히지 않는다.
              onClick={() => {
                logIxdEditorAction("최종 검토 하러가기");
                window.open(existingReportEntryUrl(reportId), "_blank");
              }}
            >
              최종 검토 하러가기
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
